import React from 'react'
import { useKey } from 'react-use'

import { useForTablet } from 'components/Breakpoint'
import Navbar, { NavbarFilterProps } from 'components/Navbar'
import NavigationSelector from 'components/NavigationSelector'

import {
  useGetDocumentListSettingsQuery,
  WorkspaceMinimalFragment,
} from 'gqlTypes'

// Here DocumentLayout are used in non exhaustive way
// therefore build should not break after SketchQL deployments
// eslint-disable-next-line no-restricted-imports
import { DocumentLayout } from 'gqlTypes/expansiveTypes'

import { keyWithoutModifier } from 'utils/keyboardEvents'
import { shortcuts } from 'utils/shortcuts'

import { ReactComponent as BulletList } from 'icons/bullet-list-24.svg'
import { ReactComponent as SquareGrid } from 'icons/square-grid-24.svg'

import { SearchBarWrapper, ViewAsText } from './ShareNavbar.styles'
import { SearchBar } from '../SearchBar'

const NavbarSection = Navbar.Section

const DOCUMENT_LAYOUT = [
  {
    type: 'icon' as const,
    label: 'Grid Layout',
    value: 'GRID' as const,
    icon: SquareGrid,
    shortcut: shortcuts.gridView,
  },
  {
    type: 'icon' as const,
    label: 'List Layout',
    value: 'LIST' as const,
    icon: BulletList,
    shortcut: shortcuts.listView,
  },
]

interface ShareNavbarProps {
  onSearchChange?: NonNullable<NavbarFilterProps['onChange']>
  search?: NavbarFilterProps['value']
  userRole: WorkspaceMinimalFragment['userRole']
}

const ShareNavbar: React.FC<ShareNavbarProps> = props => {
  const { userRole, search, onSearchChange } = props
  const isTabletAndBigger = useForTablet()

  const { data, updateQuery } = useGetDocumentListSettingsQuery()
  const { documentsLayout } = data || {}

  /**
   * Manager shortcuts:
   * - Search (/)
   * - View as Grid ([)
   * - View as List (])
   */
  useKey(keyWithoutModifier(shortcuts.search), function () {
    const inputElement = document!.querySelector(
      'input[name="search"]'
    ) as HTMLInputElement

    // Needed to fire `focus` after the keydown, that avoids filling the search
    // with `/`
    setTimeout(function () {
      inputElement?.focus()
    }, 0)
  })

  useKey(
    keyWithoutModifier([shortcuts.gridView, shortcuts.listView]),
    function (event) {
      const radioElements = document!.querySelectorAll(
        'input[name="document layout"]'
      ) as NodeListOf<HTMLInputElement>

      const key = event.key as
        | typeof shortcuts.gridView
        | typeof shortcuts.listView
      const viewMode = DOCUMENT_LAYOUT.find(layout => layout.shortcut === key)

      if (!viewMode) {
        return
      }

      const radioElement = Array.from(radioElements).find(
        el => el.value === viewMode.value
      )

      if (radioElement) {
        updateQuery(previous => ({
          ...previous,
          documentsLayout: viewMode.value as DocumentLayout,
        }))
      }
    }
  )

  return (
    <>
      <NavbarSection align="start">
        {isTabletAndBigger && (
          <SearchBarWrapper>
            <SearchBar
              onSearchChange={onSearchChange}
              search={search}
              userRole={userRole}
            />
          </SearchBarWrapper>
        )}
      </NavbarSection>

      <NavbarSection align="end">
        {isTabletAndBigger && <ViewAsText>View as</ViewAsText>}

        <NavigationSelector
          name="document layout"
          items={DOCUMENT_LAYOUT}
          selected={documentsLayout}
          onChange={({ value }) => {
            updateQuery(previous => ({
              ...previous,
              documentsLayout: value as DocumentLayout,
            }))
          }}
        />
      </NavbarSection>
    </>
  )
}

export default ShareNavbar
